import { AfterViewInit, ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { BadgeModule } from 'primeng/badge';
import { ContextMenu } from 'primeng/contextmenu';
import { DrawerService } from '../../../../core/services/drawer.service';
import { MapContextMenuBridgeService } from '../../../../core/services/map-context-menu-bridge.service';
import { MapService } from '../../../../core/services/map.service';
import { UnitCommandsService } from '../../../../core/services/unit-commands.service';
import { TrackRegenerateComponent } from '../../../../modules/home/components/track-panel/track-regenerate/track-regenerate.component';
import { TrackReportDockService } from '../../../../modules/home/components/track-report-dock/track-report-dock.service';
import { MatDialog } from '@angular/material/dialog';
import { FormMemoryService } from '../../../../core/services/form-memory.service';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-map-context-menu',
  imports: [ContextMenu, MatIconModule, BadgeModule],
  templateUrl: './map-context-menu.html',
  styleUrl: './map-context-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapContextMenu implements AfterViewInit {
  @ViewChild('cm') cm!: ContextMenu;

  items: any[] = [];
  selectedId?: number;
  submenuObserver?: MutationObserver;

  memory: any;

  // Services
  private dialog = inject(MatDialog);
  private bridgeServices = inject(MapContextMenuBridgeService);
  private drawerServices = inject(DrawerService);
  private mapServices = inject(MapService);
  private unitCommandsService = inject(UnitCommandsService);
  private readonly formMemoryServices = inject(FormMemoryService);
  private trackReportDock = inject(TrackReportDockService);

  ngOnInit() {
    this.memory = this.formMemoryServices.loadForm('dashboard:map');
  }

  ngAfterViewInit() {
    this.bridgeServices.register(this);
  }

  show(event: MouseEvent, unitId: number) {
    this.selectedId = unitId;
    this.buildMenu();
    this.cm.target = event.currentTarget as HTMLElement;
    this.cm.show(event);
  }

  private buildMenu() {
    const selectedUnit = this.mapServices.getCurrentLocations().find((u: any) => u.unitId === this.selectedId);

    const available = this.unitCommandsService.getAvailableCommands(selectedUnit?.availabelCommands || {});

    const commandItems = available.map((command) => ({
      label: command.label,
      icon: command.icon,
      command: () => command.action({ unitId: this.selectedId!, unitName: selectedUnit?.unitName }),
    }));

    this.items = [];

    if (commandItems.length) {
      this.items.push({
        label: 'Comandos',
        icon: 'satellite_alt',
        items: commandItems,
      });
    }

    // Units report `hasCamera` in the locations payload. Treat a missing flag as
    // "may have cameras" so the option is not silently hidden when it is absent.
    if (selectedUnit?.hasCamera !== false) {
      this.items.push({
        label: 'Cámaras',
        icon: 'video_camera_back',
        command: () => this.onOpenCameras(),
      });
    }

    this.items.push({
      label: 'Recorrido',
      icon: 'route',
      items: [
        {
          label: 'Hoy',
          icon: 'today',
          command: () => this.onGetTrack('today'),
        },
        {
          label: 'Personalizado',
          icon: 'date_range',
          command: () => this.onGetTrack('custom'),
        },
      ],
    });
  }

  onHide() {
    this.selectedId = undefined;
  }

  onShow() {
    setTimeout(() => {
      const menu = document.querySelector('.p-contextmenu');

      if (!menu) return;

      this.submenuObserver?.disconnect();

      this.submenuObserver = new MutationObserver((mutations) => {
        const submenus = document.querySelectorAll('.p-contextmenu-submenu');

        submenus.forEach((submenu) => {
          const el = submenu as HTMLElement;
          const rect = el.getBoundingClientRect();

          if (rect.bottom > window.innerHeight) {
            const offset = rect.bottom - window.innerHeight + 10;
            const currentTop = parseFloat(getComputedStyle(el).top || '0');

            if (!isNaN(currentTop)) {
              el.style.top = `${currentTop - offset}px`;
            }
          }

          // Flip submenu to the left of its parent if it would overflow the viewport's right edge.
          if (rect.right > window.innerWidth) {
            const parent = el.parentElement?.closest('.p-contextmenu, .p-contextmenu-submenu') as HTMLElement | null;
            const parentWidth = parent?.getBoundingClientRect().width ?? rect.width;
            el.style.left = `-${parentWidth}px`;
          }
        });
      });

      this.submenuObserver.observe(menu, {
        childList: true,
        subtree: true,
      });
    }, 1);
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private onOpenCameras(): void {
    if (!this.selectedId) return;
    this.drawerServices.openCameras(this.selectedId);
  }

  private onGetTrack(time: 'today' | 'custom'): void {
    if (!this.selectedId) return;

    if (time === 'today') {
      const now = new Date();
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      const startOfNextDay = new Date(startOfDay);
      startOfNextDay.setDate(startOfNextDay.getDate() + 1);
      const range = { from: startOfDay.toISOString(), to: startOfNextDay.toISOString() };

      const generatedUid = this.generateUUID();
      this.openReportWhenRouteReady(generatedUid);
      this.mapServices.addRoute(this.selectedId, range, generatedUid);
    }

    if (time === 'custom') {
      this.dialog.open(TrackRegenerateComponent, {
        width: '31.25rem',
        autoFocus: false,
        data: { unitId: this.selectedId, openReportOnSuccess: true },
      });
    }
  }

  private openReportWhenRouteReady(uid: string): void {
    this.mapServices.routes$
      .pipe(
        filter((routes: any[]) => routes.some((r) => r.uid === uid)),
        take(1),
      )
      .subscribe((routes: any[]) => {
        const track = routes.find((r) => r.uid === uid);
        if (track) this.trackReportDock.openReport(track);
      });
  }
}
